import React from "react";
import { Trophy, Target, Shield, Lightning } from "@phosphor-icons/react";
import { getContestMarketing } from "@/constants/contestMarketing";

/**
 * Preset-aware confidence copy block for the Smart Optimizer.
 *
 * Rendered right below the contest picker. Automatically swaps between
 * Millionaire Blueprint (milly), Field-Beater (GPP), Cash Game Protocol,
 * and Single-Game Combat (showdown) framing based on the currently-
 * selected preset. A cash user never sees Milly lingo; a Milly user
 * never sees cash lingo. Cross-contamination breaks trust.
 */
const TONE_MAP = {
  trophy:  { Icon: Trophy,    color: "#FFD700", border: "border-yellow-400/40", bg: "bg-yellow-500/5",  glow: "shadow-[0_0_20px_rgba(255,215,0,0.15)]" },
  target:  { Icon: Target,    color: "#39FF14", border: "border-[#39FF14]/40",  bg: "bg-[#39FF14]/5",   glow: "shadow-[0_0_20px_rgba(57,255,20,0.12)]" },
  shield:  { Icon: Shield,    color: "#38BDF8", border: "border-sky-400/40",    bg: "bg-sky-500/5",     glow: "shadow-[0_0_20px_rgba(56,189,248,0.12)]" },
  captain: { Icon: Lightning, color: "#F59E0B", border: "border-amber-400/40",  bg: "bg-amber-500/5",   glow: "shadow-[0_0_20px_rgba(245,158,11,0.12)]" },
};

export default function ConfidenceCopy({ presetKey }) {
  const pack = getContestMarketing(presetKey);
  const tone = TONE_MAP[pack.tone] || TONE_MAP.target;
  const { Icon } = tone;

  return (
    <div
      data-testid={`confidence-copy-${presetKey}`}
      className={`border ${tone.border} ${tone.bg} ${tone.glow} p-4 sm:p-5 transition-all`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="mt-0.5 flex-shrink-0" style={{ color: tone.color }}>
          <Icon size={22} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] mono uppercase tracking-[0.25em] mb-1.5 leading-none"
            style={{ color: tone.color }}
          >
            {pack.tier}
          </div>
          <div className="heading text-lg sm:text-xl font-black uppercase leading-[1.15] tracking-tight text-white mb-2.5">
            {pack.headline}
          </div>
          <div className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
            {pack.sub}
          </div>
        </div>
      </div>

      {pack.pillars && pack.pillars.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
          {pack.pillars.map((p) => (
            <div
              key={p.name}
              className="border border-zinc-800 bg-black/40 p-2.5 hover:border-zinc-700 transition-colors"
            >
              <div
                className="text-[9px] mono uppercase tracking-widest mb-1.5 font-bold leading-tight"
                style={{ color: tone.color }}
              >
                {p.name}
              </div>
              <div className="text-[10px] text-zinc-500 leading-[1.4]">
                {p.desc}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
